import { Link } from 'react-router-dom';
import Header from '../components/Header';

const S = ({ n, title, children }) => (
  <section className="mb-10">
    <h2 className="text-lg md:text-lg font-bold text-neutral-900 mb-3">{n}. {title}</h2>
    <div className="space-y-3 text-neutral-600 text-sm md:text-base leading-relaxed">{children}</div>
  </section>
);

const Sub = ({ title, children }) => (
  <div className="mt-4">
    <h3 className="font-semibold text-neutral-800 mb-2">{title}</h3>
    <div className="space-y-3 text-neutral-600 text-sm md:text-base leading-relaxed">{children}</div>
  </div>
);

const UL = ({ items }) => (
  <ul className="list-disc pl-6 space-y-1.5">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

const ContactBox = () => (
  <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-5 space-y-1">
    <p><span className="font-semibold text-neutral-800">Organization:</span> Spinser Inc.</p>
    <p><span className="font-semibold text-neutral-800">Email:</span> <a href="mailto:info@tryspinser.com" className="text-[#FF3B30] hover:underline">info@tryspinser.com</a></p>
    <p><span className="font-semibold text-neutral-800">Website:</span> tryspinser.com</p>
  </div>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div data-testid="privacy-page" className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <p className="text-xs font-semibold tracking-widest text-[#FF3B30] uppercase mb-3">Spinser Inc. | Legal Policies</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-2">Privacy Policy</h1>
        <p className="text-neutral-500 mb-1">How Spinser collects, uses, discloses, retains and protects personal information.</p>
        <p className="text-sm text-neutral-400 mb-6">Effective July 23, 2026 &middot; Last updated July 23, 2026</p>

        <div className="flex gap-4 mb-10 text-sm">
          <Link to="/terms" data-testid="privacy-to-terms-link" className="text-[#FF3B30] font-medium hover:underline">Terms and Conditions</Link>
          <a href="#cookie-policy" className="text-[#FF3B30] font-medium hover:underline">Cookie Policy</a>
        </div>

        <div className="space-y-3 text-neutral-600 text-sm md:text-base leading-relaxed mb-10">
          <p>Spinser Inc. ("Spinser," "we," "us" or "our") respects your privacy.</p>
          <p>This Privacy Policy explains how we collect, use, disclose, retain and protect personal information when you use tryspinser.com, participate in a SponsoredSpin, interact with a Spinser sponsor or contractor, use a referral link, communicate with us or otherwise use the Spinser platform and services.</p>
        </div>

        <S n={1} title="Scope">
          <p>This Privacy Policy applies to personal information handled by Spinser through:</p>
          <UL items={[
            'Our websites and applications;',
            'Campaign and giveaway entry forms;',
            'Sponsor, contractor and realtor portals;',
            'Email, text and other communications;',
            'Customer-support interactions;',
            'Referral and affiliate programs;',
            'Winner verification and prize fulfilment;',
            'Events, interviews, recordings and promotional content; and',
            'Other services displaying or referring to this Policy.',
          ]} />
          <p>A Campaign may include an additional privacy notice explaining Campaign-specific information practices. That notice will supplement this Policy.</p>
        </S>

        <S n={2} title="Personal Information We Collect">
          <p>Depending on how you interact with Spinser, we may collect the following categories of information.</p>
          <Sub title="Contact and identity information">
            <UL items={[
              'Name;', 'Email address;', 'Telephone number;', 'Date of birth or age confirmation;',
              'Province, territory, state or country;', 'Mailing or property address;', 'Account credentials; and',
              'Government-issued identification when reasonably required to verify a potential winner.',
            ]} />
            <p>We ask that identification documents only be provided through an approved secure method.</p>
          </Sub>
          <Sub title="Property and project information">
            <UL items={[
              'Property type;', 'Ownership or occupancy status;', 'General property condition;',
              'Renovation or home-service interests;', 'Requested project details;', 'Photographs or videos of a property;',
              'Approximate project value or budget;', 'Desired timing;',
              'Permission from co-owners, landlords or condominium corporations; and',
              'Information needed to assess whether a property or project qualifies.',
            ]} />
          </Sub>
          <Sub title="Campaign information">
            <UL items={[
              'Campaigns entered;', 'Entry dates and methods;', 'Referral links and referral activity;',
              'Bonus-entry activity;', 'Eligibility responses;', 'Skill-testing responses;', 'Winner-selection records;',
              'Verification documents;', 'Prize-acceptance information; and', 'Communications relating to the Campaign.',
            ]} />
          </Sub>
          <Sub title="Sponsor and business information">
            <p>For sponsors, real estate professionals, contractors and other business users, we may collect:</p>
            <UL items={[
              'Business and representative names;', 'Business contact information;', 'Service area;',
              'Licence, certification or insurance information;', 'Sponsorship preferences;',
              'Campaign purchases and payment records;', 'Portal activity;', 'Lead assignments;',
              'Performance information; and', 'Contract or billing information.',
            ]} />
            <p>Payment-card information may be processed directly by a payment provider rather than stored by Spinser.</p>
          </Sub>
          <Sub title="Device and usage information">
            <p>When you use the Platform, we may automatically collect:</p>
            <UL items={[
              'IP address;', 'Device and browser type;', 'Operating system;', 'Device identifiers;', 'Pages viewed;',
              'Links clicked;', 'Entry source or affiliate link;', 'Referring website;',
              'Approximate location derived from an IP address;', 'Session dates and times;',
              'Cookie and similar-technology information; and', 'Security, error and diagnostic logs.',
            ]} />
          </Sub>
          <Sub title="Communications and content">
            <p>We may collect messages, support requests, survey responses, reviews, testimonials, photographs, video, audio and other materials you provide.</p>
          </Sub>
          <Sub title="Inferences and matching information">
            <p>We may create inferences, categories or scores based on information you provide and your interactions with Spinser. For example, we may use information to:</p>
            <UL items={[
              'Match participants with geographically relevant Campaigns or sponsors;',
              'Estimate project timing or moving intent;',
              'Identify likely home-service interests;',
              'Detect duplicate or fraudulent entries;',
              'Personalize communications; and',
              'Prioritize appropriate follow-up.',
            ]} />
            <p>We do not use these scores to make decisions that produce legal or similarly significant effects without providing any rights required by applicable law.</p>
          </Sub>
        </S>

        <S n={3} title="How We Collect Information">
          <p>We may collect information:</p>
          <UL items={[
            'Directly from you;',
            'From a person who uses your authorized referral link;',
            'From sponsors, contractors or service providers involved in a Campaign;',
            'From social-media or advertising platforms;',
            'From identity, address or property-verification providers;',
            'From publicly available property or business records;',
            'From analytics and technology providers; and',
            'Automatically through cookies and similar technologies.',
          ]} />
          <p>Where required, we will obtain consent before collecting information from another source or using it for a new purpose.</p>
        </S>

        <S n={4} title="How We Use Personal Information">
          <p>We may use personal information to:</p>
          <UL items={[
            'Operate and improve the Platform;', 'Process Campaign entries;', 'Determine eligibility;',
            'Administer referrals and bonus entries;', 'Prevent fraud and maintain Campaign fairness;',
            'Select and verify potential winners;', 'Arrange property assessments and prize fulfilment;',
            'Match users with local Campaigns, sponsors or service providers;', 'Provide sponsor and contractor portals;',
            'Respond to inquiries and support requests;', 'Send transactional and Campaign communications;',
            'Send marketing communications where permitted;', 'Personalize content and measure Campaign performance;',
            'Conduct analytics and research;', 'Secure the Platform;', 'Enforce our agreements and Official Rules;',
            'Maintain records of consent and Campaign administration;',
            'Comply with legal, regulatory, tax and reporting obligations; and',
            'Establish, exercise or defend legal claims.',
          ]} />
          <p>We will not use personal information for a materially different purpose without providing any notice or consent required by law.</p>
        </S>

        <S n={5} title="How We Share Personal Information">
          <p>We may disclose personal information in the following circumstances.</p>
          <Sub title="Campaign sponsors and assigned real estate professionals">
            <p>When you enter a sponsored Campaign, information may be shared with the sponsor, co-sponsors or geographically assigned real estate professional where:</p>
            <UL items={[
              'The Campaign entry form or Official Rules disclose the sharing;',
              'You provide the required consent;',
              'The sharing is reasonably necessary to administer the Campaign; or',
              'Applicable law otherwise permits it.',
            ]} />
            <p>Information shared may include your name, contact details, region, property interests, project interests, stated timing and Campaign activity.</p>
            <p>Spinser does not provide every entrant's information to every sponsor. Geographic, campaign and consent-based restrictions may be used to determine which sponsor receives access.</p>
            <p>Sponsors may only use information for authorized purposes and must comply with their Spinser agreement and applicable privacy and marketing laws.</p>
          </Sub>
          <Sub title="Contractors and prize-fulfilment providers">
            <p>If you are selected as a potential winner or request a service connection, we may share necessary information with contractors, inspectors, suppliers, delivery providers or other professionals involved in assessing or completing the project.</p>
          </Sub>
          <Sub title="Technology and service providers">
            <p>We may use providers that support website and application hosting; databases and cloud storage; security and fraud detection; email and text delivery; analytics; mapping and geographic matching; identity or address verification; payments and accounting; customer relationship management; random selection or Campaign administration; document signing; and customer support.</p>
            <p>These providers may process information only for authorized services and subject to appropriate contractual restrictions.</p>
          </Sub>
          <Sub title="Legal and safety purposes">
            <p>We may disclose information where reasonably necessary to comply with law, court orders or lawful government requests; investigate fraud or security incidents; protect a person's safety; enforce our agreements; protect Spinser's rights and property; or establish, exercise or defend legal claims.</p>
          </Sub>
          <Sub title="Business transactions">
            <p>Information may be disclosed in connection with a financing, investment, merger, acquisition, reorganization, sale of assets or similar transaction. Where appropriate, recipients will be required to protect the information and use it only for the transaction or continued operation of the business.</p>
          </Sub>
          <Sub title="With your direction or consent">
            <p>We may share information when you request, direct or consent to the disclosure.</p>
          </Sub>
        </S>

        <S n={6} title="Selling and Sharing Personal Information">
          <p>Spinser does not sell personal information for monetary payment.</p>
          <p>Some analytics, advertising or attribution technologies may transmit online identifiers, cookie information or internet activity to third parties. Depending on the technology and applicable law, this activity may be considered a "sale," "sharing" or use for targeted advertising even when no money is exchanged.</p>
          <p>Where required, Spinser will provide an opportunity to opt out through its cookie or privacy controls and will recognize legally valid browser-based preference signals.</p>
          <p>Spinser does not knowingly sell or share the personal information of children.</p>
        </S>

        <S n={7} title="Campaign Benefits and Financial Incentives">
          <p>Campaigns may offer free entries, bonus entries, prizes or other benefits in connection with information you provide or actions you take. The applicable Official Rules or a separate notice will describe:</p>
          <UL items={[
            'The material terms of the offer;',
            'The information connected with the benefit;',
            'How to participate;',
            'How to withdraw, where applicable; and',
            'Any additional disclosures required by law.',
          ]} />
          <p>Participation is voluntary. Withdrawing from an incentive may make you ineligible for entries or benefits that depend on the associated information or action.</p>
        </S>

        <S n={8} title="Marketing Communications">
          <p>We may send marketing emails or text messages where you have consented or where otherwise permitted by applicable law. Marketing consent is separate from consent to receive necessary entry, security, winner-verification or prize-fulfilment communications.</p>
          <p>You may unsubscribe by using the unsubscribe link in an email; following the instructions in a text message; or contacting <a href="mailto:info@tryspinser.com" className="text-[#FF3B30] hover:underline">info@tryspinser.com</a>.</p>
          <p>We may retain limited information on a suppression list to ensure that we honour your request. A sponsor that receives information with your consent is responsible for honouring unsubscribe requests relating to its own communications.</p>
        </S>

        <S n={9} title="Cookies and Similar Technologies">
          <p>Spinser uses cookies, pixels, local storage and similar technologies for functions such as keeping the Platform secure; remembering preferences; measuring traffic and Campaign performance; attributing entries to sponsors or referral links; preventing duplicate or fraudulent entries; and delivering or measuring advertising where enabled.</p>
          <p>Additional information appears in the <a href="#cookie-policy" className="text-[#FF3B30] hover:underline">Spinser Cookie Policy</a> below. Where required, we will obtain consent before using non-essential technologies.</p>
        </S>

        <S n={10} title="Data Retention">
          <p>Spinser retains personal information only for as long as reasonably necessary for the purposes described in this Policy, including:</p>
          <UL items={[
            'Administering Campaigns and prizes;',
            'Maintaining consent, entry and winner-selection records;',
            'Providing services;', 'Resolving disputes;', 'Preventing fraud;', 'Enforcing agreements;',
            'Meeting tax, accounting, legal and regulatory requirements; and',
            'Maintaining appropriate business records.',
          ]} />
          <p>Retention periods may vary depending on the information, Campaign, legal requirements and potential disputes. When information is no longer required, we will delete, anonymize or securely isolate it, subject to backup and legal-retention requirements.</p>
        </S>

        <S n={11} title="Security">
          <p>Spinser uses administrative, technical and organizational safeguards designed to protect personal information from unauthorized access, loss, misuse, alteration and disclosure.</p>
          <p>Safeguards may include access controls, encryption, account authentication, logging, data minimization, staff restrictions and vendor-management practices. No website, network, storage system or transmission method can be guaranteed to be completely secure.</p>
          <p>You are responsible for maintaining the confidentiality of your account credentials and notifying us of suspected unauthorized activity.</p>
        </S>

        <S n={12} title="International and Cross-Border Processing">
          <p>Spinser and its service providers may process or store information in Canada, the United States or other jurisdictions.</p>
          <p>Information processed outside your province, state or country may be subject to the laws of the jurisdiction where it is processed and may be accessible to courts, governments or law-enforcement authorities in that jurisdiction. Spinser remains responsible for personal information under its control as required by applicable law.</p>
        </S>

        <S n={13} title="Your Privacy Choices and Rights">
          <p>Depending on where you live and subject to legal exceptions, you may have the right to:</p>
          <UL items={[
            'Request access to personal information;', 'Request correction of inaccurate information;',
            'Request deletion;', 'Withdraw consent;', 'Object to or restrict certain processing;',
            'Receive a portable copy of certain information;', 'Opt out of targeted advertising;',
            'Opt out of a sale or sharing of personal information;', 'Opt out of certain profiling;',
            'Appeal a denied privacy request; and',
            'Receive information about categories of information collected, purposes of use and recipients.',
          ]} />
          <p>You may also unsubscribe from marketing and adjust cookie choices. Spinser will not unlawfully discriminate against you for exercising a privacy right.</p>
          <Sub title="Submitting a request">
            <p>Send requests to <a href="mailto:info@tryspinser.com" className="text-[#FF3B30] hover:underline">info@tryspinser.com</a> with the subject line "Privacy Request." Please describe the request and identify the account, Campaign or interaction involved.</p>
            <p>We may need to verify your identity before responding. Verification information will be used only to process and document the request. An authorized agent may submit a request where permitted, but we may require proof of authority and direct identity verification.</p>
            <p>Some information may be exempt from access or deletion, including information needed to complete an active Campaign or prize; detect fraud or security incidents; comply with law; maintain legally required records; exercise legal rights; or protect another person's privacy.</p>
          </Sub>
        </S>

        <S n={14} title="Consent Withdrawal">
          <p>You may withdraw consent to optional collection, use or disclosure by contacting us. Withdrawal does not affect processing that occurred before the withdrawal and may be subject to legal or contractual restrictions.</p>
          <p>Withdrawing consent may prevent Spinser from processing an entry, verifying eligibility, providing a requested service or delivering a prize where the information is necessary for that purpose.</p>
        </S>

        <S n={15} title="Children and Minors">
          <p>The Platform and Spinser Campaigns are not directed to children under 13. Campaign entrants must generally have reached the age of majority specified in the Official Rules.</p>
          <p>Spinser does not knowingly collect personal information online from children under 13 without legally required parental consent. If we learn that such information was collected improperly, we will take reasonable steps to delete it.</p>
          <p>Parents or guardians may contact <a href="mailto:info@tryspinser.com" className="text-[#FF3B30] hover:underline">info@tryspinser.com</a> with concerns.</p>
        </S>

        <S n={16} title="Photographs, Videos and Winner Publicity">
          <p>Spinser may invite entrants or winners to participate in photographs, interviews, livestreams, reaction videos, testimonials or before-and-after project content. Where required, Spinser will obtain a separate release or consent describing:</p>
          <UL items={[
            'What will be recorded;',
            'How the content may be edited;',
            'Where it may be published;',
            'Whether a name, likeness, voice or property will be shown; and',
            'Any applicable withdrawal limitations after publication or production.',
          ]} />
          <p>Declining optional promotional recording will not affect an entry unless participation is an essential, lawful and clearly disclosed condition of the Campaign.</p>
        </S>

        <S n={17} title="Third-Party Websites">
          <p>The Platform may contain links to third-party websites or services. This Policy does not govern third parties that Spinser does not control. Review their privacy policies before providing personal information.</p>
        </S>

        <S n={18} title="Changes to This Privacy Policy">
          <p>We may update this Policy to reflect changes to our services, practices or legal requirements. The revised version will be posted with a new "Last Updated" date. Where required, we will provide additional notice or seek renewed consent.</p>
        </S>

        <S n={19} title="Questions and Complaints">
          <p>Privacy questions, requests or complaints may be submitted to:</p>
          <ContactBox />
          <p>We will review privacy complaints and respond within a reasonable period. You may also have the right to contact the privacy or data-protection regulator responsible for your jurisdiction.</p>
        </S>

        {/* ==================== COOKIE POLICY ==================== */}
        <div id="cookie-policy" className="border-t-2 border-neutral-200 pt-14 mt-14">
          <p className="text-xs font-semibold tracking-widest text-[#FF3B30] uppercase mb-3">Spinser Inc. | Legal Policies</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-2">Cookie Policy</h1>
          <p className="text-neutral-500 mb-1">How Spinser uses cookies and similar technologies on its websites, applications and portals.</p>
          <p className="text-sm text-neutral-400 mb-10">Effective July 23, 2026 &middot; Last updated July 23, 2026</p>

          <div className="space-y-3 text-neutral-600 text-sm md:text-base leading-relaxed mb-10">
            <p>This Cookie Policy explains how Spinser Inc. ("Spinser," "we," "us" or "our") uses cookies and similar technologies on tryspinser.com and other Spinser websites, applications and portals. This Cookie Policy should be read with the Spinser Privacy Policy above.</p>
          </div>

          <S n={1} title="What Are Cookies?">
            <p>Cookies are small text files placed on a browser or device when a person visits a website. Cookies can help a website:</p>
            <UL items={[
              'Recognize a browser or device;', 'Remember preferences;', 'Maintain security;',
              'Understand how visitors use the site;', 'Attribute entries to referral or sponsor links; and',
              'Deliver relevant content.',
            ]} />
            <p>Cookies may be set directly by Spinser as "first-party cookies" or by service providers as "third-party cookies."</p>
          </S>

          <S n={2} title="Similar Technologies">
            <p>Spinser may also use technologies that function similarly to cookies, including web pixels; tags; local browser storage; software development kits; device identifiers; tracking links; server-side logs; and advertising or analytics identifiers.</p>
            <p>References to "cookies" in this Policy include these similar technologies where appropriate.</p>
          </S>

          <S n={3} title="Categories of Cookies">
            <Sub title="Strictly necessary cookies">
              <p>These cookies support essential Platform operations, including page navigation; account login; security; load balancing; fraud prevention; entry submission; consent-preference storage; and protection against malicious activity. The Platform may not function properly without these cookies.</p>
            </Sub>
            <Sub title="Functional cookies">
              <p>Functional cookies remember choices and provide enhanced features, such as language or region; saved form information; display preferences; account settings; and customer-support features. Disabling these cookies may limit some functionality.</p>
            </Sub>
            <Sub title="Analytics and performance cookies">
              <p>These cookies help us understand how the Platform is used, including pages visited; time spent on pages; entry-form completion; errors encountered; general visitor region; website performance; and Campaign traffic sources. We use this information to improve the Platform and measure Campaign performance.</p>
            </Sub>
            <Sub title="Referral and attribution cookies">
              <p>These cookies help Spinser determine whether a visitor arrived through a sponsor link; a realtor link; a contractor link; an affiliate link; a participant referral link; an advertisement; or another Campaign source.</p>
              <p>They may be used to assign entries, calculate referral activity, measure performance and ensure that leads are associated with the appropriate Campaign or geographic sponsor.</p>
            </Sub>
            <Sub title="Advertising cookies">
              <p>Where enabled, these cookies may be used to measure advertisements; limit how often an advertisement is shown; create audiences; personalize advertising; understand whether an advertisement led to a visit or entry; and deliver advertising on third-party platforms.</p>
              <p>Depending on applicable law, the use of advertising cookies may be considered targeted advertising, sharing or a sale of personal information. Where required, these cookies will not be activated until appropriate consent is obtained.</p>
            </Sub>
          </S>

          <S n={4} title="Information Collected Through Cookies">
            <p>Cookies and similar technologies may collect:</p>
            <UL items={[
              'IP address;', 'Browser and device type;', 'Operating system;', 'Device or cookie identifiers;',
              'Approximate location;', 'Referring website;', 'Pages viewed;', 'Links selected;',
              'Date and time of access;', 'Session duration;', 'Sponsor, Campaign or referral identifiers;',
              'Entry and conversion events; and', 'Advertising interactions.',
            ]} />
            <p>This information may be associated with an account or Campaign entry where permitted and reasonably necessary.</p>
          </S>

          <S n={5} title="Session and Persistent Cookies">
            <p>Session cookies expire when you close your browser or shortly afterward. Persistent cookies remain for a set period or until deleted. Their duration depends on their purpose and the provider that sets them.</p>
            <p>We seek to retain cookies only for as long as reasonably necessary for their stated purpose.</p>
          </S>

          <S n={6} title="Third-Party Cookies">
            <p>Third-party technology providers may set or access cookies when their services are used on the Platform. These providers may include companies supporting hosting and security; analytics; advertising; social media; customer support; payment processing; mapping; video; fraud detection; and Campaign attribution.</p>
            <p>Third-party providers control their own cookies and may process information under their privacy policies. Spinser does not control cookies placed directly through third-party websites that you visit.</p>
          </S>

          <S n={7} title="Your Cookie Choices">
            <Sub title="Cookie settings">
              <p>Where a cookie banner or "Cookie Settings" control is displayed, you can use it to accept, reject or adjust non-essential cookie categories. Strictly necessary cookies generally cannot be disabled through the consent tool because they support core Platform functions.</p>
            </Sub>
            <Sub title="Browser controls">
              <p>Most browsers allow you to view stored cookies; delete cookies; block cookies; block third-party cookies; or receive a warning before a cookie is placed.</p>
              <p>Blocking all cookies may prevent entry forms, accounts, referral attribution or other Platform functions from working correctly. Cookie preferences may need to be set separately for each browser or device.</p>
            </Sub>
            <Sub title="Advertising controls">
              <p>Advertising platforms and industry organizations may provide additional tools for controlling interest-based advertising. These controls may not prevent advertisements from appearing, but may reduce personalization.</p>
            </Sub>
            <Sub title="Global Privacy Control">
              <p>Where required by applicable law, Spinser will recognize a valid Global Privacy Control or similar legally recognized browser signal as an opt-out request for the browser or device transmitting the signal.</p>
            </Sub>
          </S>

          <S n={8} title="Consent">
            <p>Where consent is required, Spinser will request consent before activating non-essential cookies. You may withdraw or change your cookie consent through the Cookie Settings control where available. Withdrawal will not affect processing that occurred before your preference was changed.</p>
            <p>Some cookies may be used without consent where they are strictly necessary, permitted by applicable law or required for security, fraud prevention or a service you requested.</p>
          </S>

          <S n={9} title="Referral Links and Bonus Entries">
            <p>Spinser may use referral identifiers, cookies or similar technologies to determine whether a person entered through another participant's authorized referral link. Referral information may be used to:</p>
            <UL items={[
              'Apply eligible bonus entries;',
              'Detect duplicate or fraudulent referrals;',
              'Enforce referral limits;',
              'Associate an entrant with the proper Campaign; and',
              'Measure Campaign growth.',
            ]} />
            <p>Referral cookies do not guarantee that a referral or bonus entry will qualify. All entries remain subject to verification and the applicable Official Rules.</p>
          </S>

          <S n={10} title="Cookie List">
            <p>The specific cookies used by Spinser may change as the Platform and its service providers change. Where available, the current cookie list, provider, purpose, category and duration will be shown in the Platform's Cookie Settings tool.</p>
          </S>

          <S n={11} title="Changes to This Cookie Policy">
            <p>We may update this Cookie Policy when our technology, service providers or legal obligations change. The revised Policy will be posted with a new "Last Updated" date. Where required, we will provide notice or request renewed consent.</p>
          </S>

          <S n={12} title="Contact">
            <p>Questions about this Cookie Policy may be directed to:</p>
            <ContactBox />
          </S>
        </div>

        <div className="border-t border-neutral-200 pt-8 mt-4 flex gap-6">
          <Link to="/" data-testid="privacy-back-home-link" className="text-[#FF3B30] font-medium hover:underline">&larr; Back to Home</Link>
          <Link to="/terms" className="text-[#FF3B30] font-medium hover:underline">Terms and Conditions</Link>
        </div>
      </div>
    </div>
  );
}
